import { useEffect, useState, useCallback } from "react";
import { Loader2, Search, Eye, EyeOff, Trash2, MessageSquare, X } from "lucide-react";
import { toast } from "sonner";
import { api, mediaSrc } from "@/lib/api";
import { OVERLINE, H1 } from "@/components/admin/ui";

const TABS = [["all", "All"], ["visible", "Visible"], ["hidden", "Hidden"]];

function CommentsModal({ postId, onClose }) {
  const [post, setPost] = useState(null);
  const load = useCallback(() => { api.modGetPost(postId).then(setPost).catch(() => setPost(null)); }, [postId]);
  useEffect(() => { load(); }, [load]);
  const hide = async (c) => { try { await api.modHideComment(postId, c.id, !c.hidden); load(); } catch { toast.error("Failed"); } };
  const del = async (c) => { if (!window.confirm("Delete this comment permanently?")) return; try { await api.modDeleteComment(postId, c.id); load(); toast.success("Comment deleted"); } catch { toast.error("Failed"); } };
  const comments = post?.comments || [];
  return (
    <div className="fixed inset-0 z-[70] bg-black/45 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-[#F9F8F6] w-full max-w-lg max-h-[85vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="mod-comments-modal">
        <div className="flex items-center justify-between mb-4"><h2 className="font-serif text-2xl text-[#1F2A22]">Comments</h2><button onClick={onClose}><X size={20} /></button></div>
        {post === null ? <Loader2 className="animate-spin text-[#FF6A00]" /> : comments.length === 0 ? (
          <p className="text-sm text-[#5C6B61]">No comments on this post.</p>
        ) : (
          <div className="space-y-2">
            {comments.map((c) => (
              <div key={c.id} className={`border p-3 ${c.hidden ? "border-[#8A5A44]/40 bg-[#8A5A44]/5" : "border-[#E5E2DC] bg-white"}`} data-testid={`mod-comment-${c.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0"><p className="text-xs font-medium text-[#1F2A22]">{c.author_name}{c.hidden && <span className="ml-2 text-[10px] uppercase text-[#8A5A44]">Hidden</span>}</p><p className="text-sm text-[#3A4740] break-words">{c.text}</p></div>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => hide(c)} className="p-1 hover:bg-[#E5E2DC]" title={c.hidden ? "Unhide" : "Hide"} data-testid={`mod-comment-hide-${c.id}`}>{c.hidden ? <Eye size={14} /> : <EyeOff size={14} />}</button>
                    <button onClick={() => del(c)} className="p-1 hover:bg-red-50 text-red-600" title="Delete" data-testid={`mod-comment-delete-${c.id}`}><Trash2 size={14} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AdminArchive() {
  const [rows, setRows] = useState(null);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [commentsFor, setCommentsFor] = useState(null);

  const load = useCallback(() => { setRows(null); api.modListPosts(tab, q).then(setRows).catch(() => setRows([])); }, [tab, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const hide = async (p) => { try { await api.modHidePost(p.id, !p.hidden); setRows((r) => r.map((x) => (x.id === p.id ? { ...x, hidden: !p.hidden } : x))); toast.success(p.hidden ? "Post is visible again" : "Post hidden from public"); } catch { toast.error("Failed"); } };
  const del = async (p) => { if (!window.confirm("Permanently delete this post? This cannot be undone.")) return; try { await api.modDeletePost(p.id); setRows((r) => r.filter((x) => x.id !== p.id)); toast.success("Post deleted"); } catch { toast.error("Failed"); } };

  return (
    <div className="p-6 md:p-8" data-testid="admin-archive">
      <div><span className={OVERLINE}>Community</span><h1 className={`${H1} mt-1`}>Archive Moderation</h1></div>
      <p className="text-sm text-[#5C6B61] mt-2 mb-5">Review community posts and comments. Hidden content stays in the database but is removed from every public feed — you can unhide it any time.</p>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8478]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search text, author, species…" className="w-full border border-[#E5E2DC] bg-white pl-9 pr-3 py-2 text-sm outline-none focus:border-[#1F2A22]" data-testid="archive-search" />
        </div>
        <div className="flex gap-1.5">
          {TABS.map(([k, l]) => <button key={k} onClick={() => setTab(k)} className={`px-3 py-1.5 text-xs border ${tab === k ? "bg-[#1F2A22] text-white border-[#1F2A22]" : "border-[#E5E2DC] text-[#5C6B61]"}`} data-testid={`archive-filter-${k}`}>{l}</button>)}
        </div>
      </div>

      {rows === null ? (
        <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-[#FF6A00]" /></div>
      ) : rows.length === 0 ? (
        <p className="text-[#5C6B61] py-10">No posts found.</p>
      ) : (
        <div className="space-y-2" data-testid="archive-list">
          {rows.map((p) => (
            <div key={p.id} className={`flex items-start gap-3 border p-3 ${p.hidden ? "border-[#8A5A44]/40 bg-[#8A5A44]/5" : "border-[#E5E2DC] bg-white"}`} data-testid={`archive-post-${p.id}`}>
              {p.media && p.media_type !== "video" && <img src={mediaSrc(p.media)} alt="" className="w-16 h-16 object-cover shrink-0 border border-[#1F2A22]/10" />}
              <div className="flex-1 min-w-0">
                <p className="text-xs text-[#8A8478]">{p.author_name}{p.species ? ` · ${p.species}` : ""}{p.hidden && <span className="ml-2 text-[10px] uppercase text-[#8A5A44] font-semibold">Hidden</span>}</p>
                <p className="text-sm text-[#1F2A22] line-clamp-2 break-words">{p.text || <span className="italic text-[#8A8478]">(media only)</span>}</p>
                <p className="text-[11px] text-[#8A8478] mt-1">{p.likes} likes · {p.comments} comments</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-1.5 shrink-0">
                {p.comments > 0 && <button onClick={() => setCommentsFor(p.id)} className="inline-flex items-center gap-1 text-xs border border-[#E5E2DC] text-[#5C6B61] px-2 py-1 hover:bg-[#E5E2DC]" data-testid={`archive-comments-${p.id}`}><MessageSquare size={13} /> {p.comments}</button>}
                <button onClick={() => hide(p)} className="inline-flex items-center gap-1 text-xs border border-[#8A5A44] text-[#8A5A44] px-2 py-1" data-testid={`archive-hide-${p.id}`}>{p.hidden ? <><Eye size={13} /> Unhide</> : <><EyeOff size={13} /> Hide</>}</button>
                <button onClick={() => del(p)} className="inline-flex items-center gap-1 text-xs border border-red-600 text-red-600 px-2 py-1" data-testid={`archive-delete-${p.id}`}><Trash2 size={13} /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {commentsFor && <CommentsModal postId={commentsFor} onClose={() => setCommentsFor(null)} />}
    </div>
  );
}
